// Shared shapes for the Playbooks page: run/phase states, playbook parsing,
// and turning agent output and tool calls into readable rows.
import { parseTs } from '../SecurityAlerts/model';

export const HOST_TOOLS = ['isolate_host', 'block_ip', 'block_domain', 'kill_process', 'quarantine_file'];

const json = (v, fallback) => {
  if (v == null || v === 'null') return fallback;
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch { return fallback; }
};

export const parseWorkflow = (w) => {
  const phases = (json(w.phases, []) || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return {
    ...w,
    agents: json(w.agents, []) || [],
    phases,
    tools_used: json(w.tools_used, []) || [],
    trigger_examples: json(w.trigger_examples, []) || [],
  };
};

export const parseAgent = (a) => ({ ...a, tools: Array.isArray(a.tools) ? a.tools : json(a.tools, []) || [] });

// Run state as the page shows it. A rejected gate leaves the run "failed"
// in the database; here it reads as Stopped.
export const runState = (run) => {
  if (run.status === 'waiting_approval') return 'waiting';
  if (run.status === 'running') return 'running';
  if (run.status === 'completed') return 'done';
  if (run.status === 'cancelled') return 'stopped';
  if (run.status === 'failed') return Number(run.rejected) ? 'stopped' : 'failed';
  return 'running';
};

export const RUN_STATE = {
  waiting: { label: 'Waiting for approval', mk: 'var(--sev-high-fg)', chip: 'var(--sev-high-bg)' },
  running: { label: 'Running', mk: 'transparent', mkB: 'var(--ink-text)', chip: 'var(--surf-raised)' },
  done: { label: 'Completed', mk: 'var(--sev-good-fg)', chip: 'var(--sev-good-bg)' },
  failed: { label: 'Failed', mk: 'var(--sev-crit-fg)', chip: 'var(--sev-crit-bg)' },
  stopped: { label: 'Stopped · rejected', mk: 'var(--sev-low-fg)', chip: 'var(--sev-low-bg)' },
  never: { label: 'Never run', mk: 'transparent', mkB: 'var(--surf-line-strong)', chip: 'var(--surf-raised)' },
};

// Phase state for one run phase. Gates are stored as approval_required from
// the start, so only the first one on a waiting run is actually "reached".
export const phaseState = (p, run, isActiveGate) => {
  if (p.status === 'completed') return 'done';
  if (p.status === 'running') return 'running';
  if (p.status === 'failed') return p.approval_status === 'rejected' ? 'rejected' : 'failed';
  if (p.status === 'approval_required') return isActiveGate && run.status === 'waiting_approval' ? 'gate' : 'pending';
  return 'pending';
};

export const barColor = (st) => ({
  done: 'var(--ink-muted)', gate: 'var(--sev-high-fg)', running: 'var(--ink-text)',
  failed: 'var(--sev-crit-fg)', rejected: 'var(--sev-low-fg)',
}[st] || 'var(--surf-line)');

// Bar segments from the list endpoint's phase_states ("completed,running,…").
export const barFromStates = (run) => {
  const states = (run.phase_states && run.phase_states !== 'null' ? run.phase_states.split(',') : []);
  let gateSeen = false;
  return states.map(s => {
    if (s === 'completed') return 'done';
    if (s === 'running') return 'running';
    if (s === 'failed') return Number(run.rejected) ? 'rejected' : 'failed';
    if (s === 'approval_required' && !gateSeen && run.status === 'waiting_approval') { gateSeen = true; return 'gate'; }
    return 'pending';
  });
};

export const fmtDur = (ms) => {
  if (ms == null || Number.isNaN(Number(ms))) return '';
  const s = Number(ms) / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  return `${m}m ${String(Math.round(s % 60)).padStart(2, '0')}s`;
};

export const runDurationMs = (run) => {
  const a = parseTs(run.started_at);
  const b = parseTs(run.completed_at);
  return a && b ? b - a : null;
};

export const stamp = (iso) => {
  const d = parseTs(iso);
  if (!d) return '';
  const today = new Date();
  const y = new Date(today); y.setDate(today.getDate() - 1);
  const hm = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (d.toDateString() === today.toDateString()) return `Today ${hm}`;
  if (d.toDateString() === y.toDateString()) return `Yesterday ${hm}`;
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} ${hm}`;
};

export const TARGET_TYPES = [
  ['finding', 'Finding'], ['ioc', 'IoC alert'], ['cve', 'CVE alert'], ['asset', 'Asset'], ['none', 'No target'],
];
export const TARGET_LABEL = { finding: 'Finding', ioc: 'IoC alert', cve: 'CVE alert', asset: 'Asset', case: 'Case', context: 'Context' };

export const targetHref = (type, id, servers = []) => {
  if (!type || !id) return null;
  if (type === 'finding') return `/findings?finding=${encodeURIComponent(id)}`;
  if (type === 'ioc' || type === 'cve') return `/security-alerts?alert=${type}-${encodeURIComponent(id)}`;
  if (type === 'case') return `/cases/${encodeURIComponent(id)}`;
  if (type === 'asset') {
    const s = servers.find(x => x.id === id || x.hostname === id || x.ip_address === id);
    return s ? `/assets/${s.id}` : null;
  }
  return null;
};

// Which registered asset an agent's free-text "affected assets" entry names,
// by hostname or IP as a whole token, so 192.168.0.1 doesn't match
// 192.168.0.10. Agents also list accounts, shares and unmanaged devices,
// which match nothing: those aren't in the inventory.
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
export const matchAsset = (text, servers = []) => {
  if (!text) return null;
  return servers.find(s => [s.hostname, s.ip_address].some(v =>
    v && v.length >= 3 && new RegExp(`(^|[^\\w.-])${escapeRe(v)}($|[^\\w.-])`, 'i').test(text),
  )) || null;
};

// Agent output is usually JSON, often inside a ```json fence.
export const parseOutput = (raw) => {
  if (!raw || raw === 'null') return { text: null, obj: null };
  const s = String(raw).trim();
  const fenced = s.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const body = fenced ? fenced[1] : s;
  try {
    const obj = JSON.parse(body);
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) return { text: null, obj };
  } catch { /* prose */ }
  return { text: s, obj: null };
};

const label = (k) => k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
const itemText = (v) => (typeof v === 'string' ? v : v && typeof v === 'object'
  ? Object.values(v).filter(x => typeof x === 'string' || typeof x === 'number').join(' · ') || JSON.stringify(v)
  : String(v));

// { summary, facts: [[k,v]], lists: [[k,[items]]], rec: [items] }
export const readableOutput = (raw) => {
  const { text, obj } = parseOutput(raw);
  if (!obj) return { summary: text, facts: [], lists: [], rec: [] };
  const keys = Object.keys(obj);
  const sumKey = keys.find(k => /summary/i.test(k) && typeof obj[k] === 'string')
    || ['assessment', 'conclusion', 'description', 'analysis'].find(k => typeof obj[k] === 'string');
  const recKey = keys.find(k => /recommend/i.test(k) && Array.isArray(obj[k]));
  const facts = [];
  const lists = [];
  keys.forEach(k => {
    if (k === sumKey || k === recKey) return;
    const v = obj[k];
    if (v == null || v === '') return;
    if (Array.isArray(v)) { if (v.length) lists.push([label(k), v.map(itemText)]); return; }
    if (typeof v === 'object') { facts.push([label(k), itemText(v)]); return; }
    facts.push([label(k), String(v)]);
  });
  return { summary: sumKey ? obj[sumKey] : null, facts, lists, rec: recKey ? obj[recKey].map(itemText) : [] };
};

export const parseToolCalls = (raw) => {
  const calls = json(raw, []) || [];
  return (Array.isArray(calls) ? calls : []).map(tc => {
    const args = tc.arguments && typeof tc.arguments === 'object'
      ? Object.entries(tc.arguments).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')
      : '';
    let res = tc.result == null ? '' : String(tc.result);
    const r = json(res, null);
    if (r && typeof r === 'object') {
      res = r.error ? `Error: ${r.error}`
        : r.message ? r.message
        : r.status && r.action_id ? `Queued ${r.status} action ${r.action_id}`
        : r.found === false ? 'Not found'
        : Array.isArray(r.matches) ? `Found · ${r.matches.length} match${r.matches.length === 1 ? '' : 'es'}`
        : r.hostname ? [r.hostname, r.ip_address, r.os_info].filter(Boolean).join(' · ')
        : Object.entries(r).slice(0, 4).map(([k, v]) => `${k}: ${typeof v === 'object' ? '…' : v}`).join(' · ');
    }
    return { name: tc.name, args, res: res.length > 220 ? `${res.slice(0, 220)}…` : res };
  });
};
